import React, {useEffect, useState} from "react";
import TextField from "@mui/material/TextField";
import {WaitTimeInput} from "../WaitTimeInput";
import Button from "@mui/material/Button";
import {useMutation} from "@apollo/client";
import {CREATE_APPROVAL_STAGE} from "../../../api/stage/createApprovalStage";
import {initialApprovalFormData} from "./AddEditModal";
import Stack from "@mui/material/Stack";
import {DialogActions, DialogContent} from "@mui/material";
import {ErrorMessage} from "../../common/ErrorMessage";
import {ApprovalStage} from "../../../api/stage/getStages";
import {UPDATE_STAGE} from "../../../api/stage/updateStage";

type ApprovalFormProps = {
    formData: ApprovalStage
    onClose: (shouldRefetch: boolean) => void,
    setLoading: (nextValue: boolean) => void,
    updateFormData: (newValues: Partial<typeof initialApprovalFormData>) => void,
}

export const ApprovalForm = (props: ApprovalFormProps) => {
    const {formData, onClose, setLoading, updateFormData} = props

    const [snackBarMessage, setSnackBarMessage] = useState('')

    const [createApprovalStage, {error: createError}] = useMutation(CREATE_APPROVAL_STAGE)
    const [updateStage, {error: updateError}] = useMutation(UPDATE_STAGE)

    const onSubmit = async () => {
        setLoading(true)

        if (formData.id === '') {
            const {errors} = await createApprovalStage({variables: {input: {
                title: formData.title,
                description: formData.description,
                rejection_message: formData.rejectionMessage,
                waitTime: formData.waitTime,
            }}})

            if (errors === undefined || errors.length === 0) {
                setSnackBarMessage('')
                onClose(true)
            }
        } else {
            const {errors} = await updateStage({variables: {input: {
                description: formData.description,
                id: formData.id,
                rejectionMessage: formData.rejectionMessage,
                title: formData.title,
                type: formData.type,
                waitTime: formData.waitTime,
            }}})

            if (errors === undefined || errors.length === 0) {
                setSnackBarMessage('')
                onClose(true)
            }
        }

        setLoading(false)
    }

    useEffect(() => {
        if (createError) setSnackBarMessage(createError.message)
        else if (updateError) setSnackBarMessage(updateError.message)
    }, [createError?.message, updateError?.message])

    return (
        <>
            <DialogContent>
                <Stack gap="1.5rem">
                    <TextField
                        label="Title"
                        onChange={(e) => updateFormData({title: e.target.value})}
                        value={formData.title}
                        variant="outlined"
                    />

                    <TextField
                        label="Description"
                        minRows={3}
                        multiline
                        onChange={(e) => updateFormData({description: e.target.value})}
                        value={formData.description}
                        variant="outlined"
                    />

                    <TextField
                        label="Rejection Message"
                        minRows={3}
                        multiline
                        onChange={(e) => updateFormData({rejectionMessage: e.target.value})}
                        value={formData.rejectionMessage}
                        variant="outlined"
                    />

                    <WaitTimeInput
                        initialValue={formData.waitTime}
                        onChange={(nextValue: number) => updateFormData({waitTime: nextValue})}
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={() => onClose(false)}
                    variant="contained"
                    color="error"
                >
                    Close
                </Button>
                <Button
                    color="success"
                    disabled={formData.title === '' || formData.description === '' || formData.rejectionMessage === '' || formData.waitTime === 0}
                    onClick={onSubmit}
                    variant="contained"
                >
                    {formData.id === '' ? 'Add' : 'Edit'}
                </Button>
            </DialogActions>

            <ErrorMessage
                handleClose={() => setSnackBarMessage('')}
                isOpen={snackBarMessage !== ''}
                message={snackBarMessage}
            />
        </>
    )
}
